import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandMark } from "@/components/site/Nav";
import AdminLeads from "@/components/admin/AdminLeads";
import AdminArticles from "@/components/admin/AdminArticles";
import AdminQuizzes from "@/components/admin/AdminQuizzes";
import AdminAccounts from "@/components/admin/AdminAccounts";
import AdminStats from "@/components/admin/AdminStats";
import { LogOut, Inbox, BookOpen, ListChecks, Users, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { key: "stats", label: "Statistiques", icon: BarChart3 },
  { key: "leads", label: "Demandes", icon: Inbox },
  { key: "articles", label: "Articles", icon: BookOpen },
  { key: "quizzes", label: "Quiz", icon: ListChecks },
  { key: "accounts", label: "Comptes", icon: Users },
];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("admin_token", data.access_token);
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#111827] p-8" data-testid="admin-login-form">
        <BrandMark small />
        <h1 className="mt-6 font-heading text-xl font-semibold text-slate-100">Administration</h1>
        <p className="mt-1 text-xs text-slate-500">Gestion des ressources et des demandes</p>
        <div className="mt-6 space-y-4">
          <input data-testid="admin-email-input" className="input-dark" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input data-testid="admin-password-input" className="input-dark" type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        {error && <p className="mt-3 text-xs text-rose-400" data-testid="admin-login-error">{error}</p>}
        <button data-testid="admin-login-submit" disabled={busy} className="btn-primary w-full mt-6 disabled:opacity-60">
          {busy ? "Connexion…" : "Se connecter"}
        </button>
        <Link to="/" className="block mt-4 text-center text-xs text-slate-500 hover:text-teal-300">← Retour au site</Link>
      </form>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null); // null=checking, false=not auth
  const [tab, setTab] = useState("stats");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("admin_token");
    setUser(false);
  };

  if (user === null)
    return <div className="min-h-screen bg-[#0b0f19] flex items-center justify-center text-slate-500">Chargement…</div>;

  if (user === false)
    return (
      <main className="bg-[#0b0f19] min-h-screen">
        <Login onLogin={setUser} />
      </main>
    );

  return (
    <main className="bg-[#0b0f19] min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0d1322]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrandMark small />
            <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-amber-400 border border-amber-400/30 px-2 py-1">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 hidden sm:block">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-rose-400 transition-colors">
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-8">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-mono2 uppercase tracking-[0.15em] border transition-colors ${
                tab === key ? "border-teal-400/60 text-teal-300 bg-teal-400/5" : "border-white/10 text-slate-500 hover:text-slate-300"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        {tab === "stats" && <AdminStats />}
        {tab === "leads" && <AdminLeads />}
        {tab === "articles" && <AdminArticles />}
        {tab === "quizzes" && <AdminQuizzes />}
        {tab === "accounts" && <AdminAccounts user={user} onUserUpdate={setUser} />}
      </div>
    </main>
  );
}
